import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus as faPlusSolid } from '@fortawesome/pro-solid-svg-icons';
import { faPen } from '@fortawesome/pro-light-svg-icons';
import { Button } from '@shared/components/ui/button';
import { ConfirmModal, ConfirmModalRef } from '@shared/components/ui/ConfirmModal';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Tags } from '@shared/components/ui/Tags';
import { ImportFilters } from './ImportFilters';
import { mapFiltersToAPI, mapFiltersToUI } from '../../utils/importFiltersMapper';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { ShopsApiAdapter } from '../../adapters/ShopsApiAdapter';
import { useToast } from '@shared/hooks/use-toast';

interface ImportFiltersSummaryProps {
  shopId: number;
  initialFilters: any;
  onFiltersUpdated?: (newFilters: any) => void;
}

export const ImportFiltersSummary = ({ shopId, initialFilters, onFiltersUpdated }: ImportFiltersSummaryProps) => {
  const { t } = useTranslation('shopsModule');
  const { toast } = useToast();
  const shopsApiAdapter = new ShopsApiAdapter(createHttpClient(apiClientWH));
  const modalRef = useRef<ModalCenterRef>(null);
  const confirmModalRef = useRef<ConfirmModalRef>(null);
  const [highlightedField, setHighlightedField] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Mapear filtros iniciales al formato de UI
  const uiFilters = mapFiltersToUI(initialFilters);
  const { include, exclude } = uiFilters;

  // Estado temporal para la modal de edición
  const [tempFilters, setTempFilters] = useState<{
    include: Record<string, string[]>;
    exclude: Record<string, string[]>;
  } | null>(null);

  const hasFilters = Object.keys(include).length > 0 || Object.keys(exclude).length > 0;

  const handleOpenModal = (fieldKey: string | null = null) => {
    setHighlightedField(fieldKey);
    setTempFilters({
      include: { ...include },
      exclude: { ...exclude },
    });
    modalRef.current?.openModal();
  };

  const handleSaveFilters = async () => {
    if (!tempFilters) return;

    setIsSaving(true);
    try {
      const apiFilters = mapFiltersToAPI(tempFilters);
      const response = await shopsApiAdapter.updateImportFilters(shopId, {
        import_filters: apiFilters,
      });

      if (response.success) {
        toast({
          title: t('modals.settings.importFilters.toast.success.title', 'Filtros actualizados'),
          description: t(
            'modals.settings.importFilters.toast.success.description',
            'Los filtros se han guardado correctamente.',
          ),
        });
        onFiltersUpdated?.(apiFilters);
        modalRef.current?.closeModal();
      } else {
        throw new Error(response.message);
      }
    } catch (error: any) {
      toast({
        title: t('common.error'),
        description:
          error.message || t('modals.settings.importFilters.toast.error.description', 'Error al guardar los filtros.'),
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveTag = (type: 'include' | 'exclude', field: string, value: string) => {
    confirmModalRef.current?.open(
      async () => {
        const currentValues = uiFilters[type][field];
        const newValues = currentValues.filter((v) => v !== value);

        const updatedUIFilters = {
          ...uiFilters,
          [type]: {
            ...uiFilters[type],
            [field]: newValues,
          },
        };

        const apiFilters = mapFiltersToAPI(updatedUIFilters);

        try {
          const response = await shopsApiAdapter.updateImportFilters(shopId, {
            import_filters: apiFilters,
          });

          if (response.success) {
            toast({
              title: t('modals.settings.importFilters.toast.tagRemoved.title', 'Tag eliminado'),
              variant: 'default',
            });
            onFiltersUpdated?.(apiFilters);
          } else {
            throw new Error(response.message);
          }
        } catch (error: any) {
          toast({
            title: t('common.error'),
            description: error.message,
            variant: 'destructive',
          });
        }
      },
      undefined,
      t('modals.settings.importFilters.confirmRemoveTagTitle', 'Eliminar filtro'),
      t(
        'modals.settings.importFilters.confirmRemoveTagMessage',
        '¿Estás seguro de que deseas eliminar este valor del filtro?',
      ),
    );
  };

  return (
    <>
      <div className="w-full">
        <div className="flex items-center gap-2 mb-4">
          <h2 className="font-medium">{t('modals.settings.importFilters.title')}</h2>
          <button
            onClick={() => handleOpenModal()}
            className="text-icons hover:text-text transition-colors flex items-center justify-center p-0.5"
          >
            <FontAwesomeIcon icon={hasFilters ? faPen : faPlusSolid} className="w-3 h-3" />
          </button>
        </div>

        <div className="flex flex-col gap-4">
          {hasFilters ? (
            <>
              {/* Inclusions Section */}
              <div className="space-y-3">
                <div className="flex items-center gap-1.5 h-4">
                  <h3 className="text-[10px] font-bold text-success uppercase tracking-wider">
                    {t('modals.settings.importFilters.include')}
                  </h3>
                  {Object.keys(include).length === 0 && (
                    <button
                      onClick={() => handleOpenModal()}
                      className="text-success/60 hover:text-success transition-colors flex items-center justify-center p-0.5 transform translate-y-[-0.5px]"
                    >
                      <FontAwesomeIcon icon={faPlusSolid} className="w-2.5 h-2.5" />
                    </button>
                  )}
                </div>
                {Object.entries(include).length > 0 && (
                  <div className="flex flex-col gap-2">
                    {Object.entries(include).map(([field, values]) => (
                      <div
                        key={`include-${field}`}
                        className="p-2.5 bg-card-bg/30 rounded-lg border border-card-border group relative"
                      >
                        <div className="flex justify-between items-start mb-1.5">
                          <div className="flex flex-col">
                            <span className="text-xs font-semibold text-text-secondary uppercase tracking-tight">
                              {t(`modals.settings.importFilters.fieldLabels.${field}`)}
                            </span>
                            <p className="text-[10px] text-complementary-text leading-tight">
                              {t(`modals.settings.importFilters.fieldDescriptions.${field}`)}
                            </p>
                          </div>
                          <button
                            onClick={() => handleOpenModal(`include-${field}`)}
                            className="opacity-100 md:opacity-0 group-hover:opacity-100 transition-opacity text-icons hover:text-text"
                          >
                            <FontAwesomeIcon icon={faPen} className="w-2.5 h-2.5" />
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {values.length > 0 ? (
                            values.map((v) => {
                              const displayValue = v;
                              return (
                                <Tags
                                  key={v}
                                  className="bg-card-border"
                                  onRemove={() => handleRemoveTag('include', field, v)}
                                >
                                  {displayValue}
                                </Tags>
                              );
                            })
                          ) : (
                            <span className="text-[10px] text-text-secondary italic">-</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Exclusions Section */}
              <div className="space-y-3">
                <div className="flex items-center gap-1.5 h-4">
                  <h3 className="text-[10px] font-bold text-error uppercase tracking-wider">
                    {t('modals.settings.importFilters.exclude')}
                  </h3>
                  {Object.keys(exclude).length === 0 && (
                    <button
                      onClick={() => handleOpenModal()}
                      className="text-error/60 hover:text-error transition-colors flex items-center justify-center p-0.5 transform translate-y-[-0.5px]"
                    >
                      <FontAwesomeIcon icon={faPlusSolid} className="w-2.5 h-2.5" />
                    </button>
                  )}
                </div>
                {Object.entries(exclude).length > 0 && (
                  <div className="flex flex-col gap-2">
                    {Object.entries(exclude).map(([field, values]) => (
                      <div
                        key={`exclude-${field}`}
                        className="p-2.5 bg-card-bg/30 rounded-lg border border-card-border group relative"
                      >
                        <div className="flex justify-between items-start mb-1.5">
                          <div className="flex flex-col">
                            <span className="text-xs font-semibold text-text-secondary uppercase tracking-tight">
                              {t(`modals.settings.importFilters.fieldLabels.${field}`)}
                            </span>
                            <p className="text-[10px] text-complementary-text leading-tight">
                              {t(`modals.settings.importFilters.fieldDescriptions.${field}`)}
                            </p>
                          </div>
                          <button
                            onClick={() => handleOpenModal(`exclude-${field}`)}
                            className="opacity-100 md:opacity-0 group-hover:opacity-100 transition-opacity text-icons hover:text-text"
                          >
                            <FontAwesomeIcon icon={faPen} className="w-2.5 h-2.5" />
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {values.length > 0 ? (
                            values.map((v) => {
                              const displayValue = v;
                              return (
                                <Tags
                                  key={v}
                                  className="bg-card-border"
                                  onRemove={() => handleRemoveTag('exclude', field, v)}
                                >
                                  {displayValue}
                                </Tags>
                              );
                            })
                          ) : (
                            <span className="text-[10px] text-text-secondary italic">-</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            <p className="text-sm text-text-secondary italic">{t('modals.settings.importFilters.noFilters')}</p>
          )}
        </div>
      </div>

      <ModalCenter
        ref={modalRef}
        title={t('modals.settings.importFilters.title')}
        type="default"
        width="xxl"
        className="bg-card-bg !outline-0"
        classNameBody="max-h-[70vh]"
        fullScreenMobile={true}
        onCloseModal={() => setHighlightedField(null)}
        footer={
          <div className="flex justify-end w-full">
            <Button variant="primary" onClick={handleSaveFilters} disabled={isSaving}>
              {isSaving ? t('common.loading') : t('modals.settings.buttons.save')}
            </Button>
          </div>
        }
      >
        <div className="pb-6 pt-4">
          {tempFilters && (
            <ImportFilters
              include={tempFilters.include}
              exclude={tempFilters.exclude}
              onChange={(type, field, values) => {
                setTempFilters((prev) =>
                  prev
                    ? {
                        ...prev,
                        [type]: {
                          ...prev[type],
                          [field]: values,
                        },
                      }
                    : null,
                );
              }}
              onRemoveField={(type, field) => {
                setTempFilters((prev) => {
                  if (!prev) return null;
                  const newData = { ...prev[type] };
                  delete newData[field];
                  return {
                    ...prev,
                    [type]: newData,
                  };
                });
              }}
              highlightedField={highlightedField}
            />
          )}
        </div>
      </ModalCenter>

      <ConfirmModal ref={confirmModalRef} />
    </>
  );
};
